import React, { useState } from 'react';
import { Input, Button, Popconfirm, message, Avatar, Empty, Space, Tooltip } from 'antd';
import {
  SendOutlined,
  DeleteOutlined,
  EditOutlined,
  CommentOutlined,
  LikeOutlined,
  LikeFilled,
  DislikeOutlined,
  DislikeFilled,
} from '@ant-design/icons';
import { stripHtml } from '../../../utils/stripHtml';
import { toJalali } from '../../../utils/dateUtils';

const { TextArea } = Input;

const formatCommentTime = (value) => {
  if (!value) return null;
  return toJalali(value, 'jYYYY/jMM/jDD HH:mm') || toJalali(value) || value;
};

const countEmoji = (list = [], emoji) =>
  (Array.isArray(list) ? list : []).filter((x) => !emoji || x.emoji_name === emoji).length;

const WorkCommentPanel = ({
  comments = [],
  onCreate,
  onUpdate,
  onDelete,
  onLike,
  currentUserId = null,
  placeholder = 'نوشتن نظر...',
}) => {
  const [text, setText] = useState('');
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');
  const [loading, setLoading] = useState(false);
  const [likeLoadingId, setLikeLoadingId] = useState(null);

  const submit = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      await onCreate(text.trim());
      setText('');
      message.success('ثبت شد');
    } catch {
      message.error('خطا');
    } finally {
      setLoading(false);
    }
  };

  const saveEdit = async () => {
    if (!editId) return;
    setLoading(true);
    try {
      await onUpdate(editId, editText);
      setEditId(null);
      message.success('ویرایش شد');
    } catch {
      message.error('خطا');
    } finally {
      setLoading(false);
    }
  };

  const toggleLike = async (comment, emojiName) => {
    if (!onLike) return;
    setLikeLoadingId(comment.id);
    try {
      await onLike(comment.id, emojiName);
    } catch {
      message.error('خطا در ثبت واکنش');
    } finally {
      setLikeLoadingId(null);
    }
  };

  const userReacted = (list = []) => {
    if (currentUserId == null) return false;
    return (Array.isArray(list) ? list : []).some(
      (x) => String(x.user_id) === String(currentUserId),
    );
  };

  return (
    <div className="space-y-4" style={{ fontFamily: 'CustomFont' }}>
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-3">
        <div className="flex gap-2 items-end">
          <TextArea
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            className="flex-1"
            style={{ fontFamily: 'CustomFont' }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={loading}
            onClick={submit}
            className="h-10"
            style={{ fontFamily: 'CustomFont' }}
          >
            ارسال
          </Button>
        </div>
      </div>

      {!comments?.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span style={{ fontFamily: 'CustomFont' }}>هنوز نظری ثبت نشده است</span>}
        />
      ) : (
        <div className="space-y-3">
          {comments.map((c) => {
            const raw = c.message || c.comment || '';
            const displayText = stripHtml(raw) || '—';
            const when =
              formatCommentTime(c.created_at) ||
              formatCommentTime(c.created_on) ||
              formatCommentTime(c.updated_at);
            const likes = c.like || c.likes || [];
            const dislikes = c.dislike || c.dislikes || [];
            const likeCount = countEmoji(likes, 'thumbs-up') || likes.length;
            const dislikeCount = countEmoji(dislikes, 'thumbs-down') || dislikes.length;
            const likedByMe = userReacted(likes);
            const dislikedByMe = userReacted(dislikes);

            return (
              <div
                key={c.id}
                className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar size={40} src={c.user?.image_url} className="flex-shrink-0 bg-teal-100 text-teal-700">
                      {c.user?.name?.[0] || <CommentOutlined />}
                    </Avatar>
                    <div className="min-w-0">
                      <div
                        className="font-semibold text-gray-800 dark:text-gray-100 truncate"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {c.user?.name || c.from || 'کاربر'}
                      </div>
                      {when ? (
                        <div className="text-xs text-gray-400 mt-0.5" style={{ fontFamily: 'CustomFont' }}>
                          {when}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {onUpdate ? (
                      <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                          setEditId(c.id);
                          setEditText(stripHtml(raw));
                        }}
                      />
                    ) : null}
                    {onDelete ? (
                      <Popconfirm
                        title={<span style={{ fontFamily: 'CustomFont' }}>حذف نظر؟</span>}
                        onConfirm={() => onDelete(c.id)}
                        okText="حذف"
                        cancelText="لغو"
                        okButtonProps={{ danger: true }}
                      >
                        <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    ) : null}
                  </div>
                </div>

                {editId === c.id ? (
                  <div className="mt-3 flex flex-col gap-2">
                    <TextArea
                      rows={3}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      style={{ fontFamily: 'CustomFont' }}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="small" onClick={() => setEditId(null)} style={{ fontFamily: 'CustomFont' }}>
                        لغو
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        loading={loading}
                        onClick={saveEdit}
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        ذخیره
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p
                    className="mt-3 mb-0 text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-7"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {displayText}
                  </p>
                )}

                {onLike ? (
                  <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <Space size="middle">
                      <Tooltip title="پسندیدن">
                        <Button
                          type="text"
                          size="small"
                          loading={likeLoadingId === c.id}
                          className={likedByMe ? 'text-blue-600' : ''}
                          icon={likedByMe ? <LikeFilled /> : <LikeOutlined />}
                          onClick={() => toggleLike(c, 'thumbs-up')}
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          {likeCount || ''}
                        </Button>
                      </Tooltip>
                      <Tooltip title="نپسندیدن">
                        <Button
                          type="text"
                          size="small"
                          loading={likeLoadingId === c.id}
                          className={dislikedByMe ? 'text-red-500' : ''}
                          icon={dislikedByMe ? <DislikeFilled /> : <DislikeOutlined />}
                          onClick={() => toggleLike(c, 'thumbs-down')}
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          {dislikeCount || ''}
                        </Button>
                      </Tooltip>
                    </Space>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkCommentPanel;
